import { useEffect, useState } from "react";
import axios from "axios";
import { FilePdf, UploadSimple, DownloadSimple, X, CheckCircle, WarningCircle, Eye, FloppyDisk } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PDF Tools → Purchase Bill Extractor.
 *
 * Bulk-uploads PDF purchase invoices (either computer-generated OR scanned
 * images), sends them through the backend pipeline that OCRs + LLM-parses
 * each bill, and returns a multi-sheet Excel workbook (Summary +
 * Line-Items + Errors).
 *
 * User can optionally toggle "Also save to Purchases module" to persist
 * every parsed bill into db.purchases so it shows up in the regular
 * Purchases list + Stock ledger.
 */
export default function PurchaseBillExtractor() {
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("");
  const [saveToPurchases, setSaveToPurchases] = useState(false);
  const [toGodown, setToGodown] = useState(false);
  const [godownName, setGodownName] = useState("");
  const [godowns, setGodowns] = useState([]);
  useEffect(() => { axios.get(`${API}/godowns`).then((r) => setGodowns(r.data.items || [])).catch(() => {}); }, []);
  const [downloadStats, setDownloadStats] = useState(null);
  const [error, setError] = useState("");
  const [jobProgress, setJobProgress] = useState(null); // {current, total, jobId}

  const addFiles = (list) => {
    const arr = Array.from(list).filter((f) => f.type === "application/pdf" || /\.pdf$/i.test(f.name));
    if (!arr.length) {
      setError("Please pick at least one .pdf file.");
      return;
    }
    setError("");
    setFiles((prev) => [...prev, ...arr]);
    setPreview(null);
    setDownloadStats(null);
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));
  const reset = () => {
    setFiles([]); setPreview(null); setDownloadStats(null); setError(""); setStep("");
  };

  const runPreview = async () => {
    if (!files.length) return;
    setBusy(true); setStep("Extracting text + running OCR fallback + parsing with AI…"); setError("");
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f, f.name));
      const r = await axios.post(`${API}/pdf-tools/purchase-bills/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setPreview(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Preview failed");
    } finally {
      setBusy(false); setStep("");
    }
  };

  const runExtractAndDownload = async () => {
    if (!files.length) return;
    setBusy(true); setStep("Uploading files one-by-one (avoids Cloudflare timeout on slow connections)…"); setError("");
    setJobProgress(null);
    try {
      // Two-phase upload — solves Cloudflare 524 on slow India internet.
      // Instead of shoving all 10-20 PDFs into one giant multipart request
      // (which can take 2-3 min on ADSL uploads and blow past the 100s
      // edge timeout), we stage each PDF as its own small HTTP request,
      // then kick off the extract job with just the list of staged IDs.
      const stagedIds = [];
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const fd = new FormData();
        fd.append("file", f, f.name);
        setStep(`Uploading ${i + 1}/${files.length}: ${f.name}`);
        const stg = await axios.post(`${API}/pdf-tools/stage-file`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 90000,  // per-file, small requests → well under CF's 100s
          onUploadProgress: (evt) => {
            if (evt.total) {
              const pct = Math.round((evt.loaded / evt.total) * 100);
              setJobProgress({ current: i, total: files.length, jobId: null, pct, fname: f.name });
            }
          },
        });
        stagedIds.push(stg.data.staged_id);
      }

      setStep("Kicking off background extraction job…");
      const kick = await axios.post(
        `${API}/pdf-tools/purchase-bills/extract-from-staged`,
        { staged_ids: stagedIds, save_to_purchases: saveToPurchases, to_godown: saveToPurchases && toGodown, godown_name: godownName },
        { headers: { "Content-Type": "application/json" }, timeout: 30000 },
      );
      const jobId = kick.data.job_id;
      setStep("Extracting pages + running OCR + AI parsing (large PDFs may take a few minutes)…");
      setJobProgress({ current: 0, total: 0, jobId });

      let done = false;
      let lastStatus = "";
      let lastJob = null;
      while (!done) {
        await new Promise((r) => setTimeout(r, 2500));
        const s = await axios.get(`${API}/pdf-tools/jobs/${jobId}`);
        const p = s.data.progress || { current: 0, total: 0 };
        setJobProgress({ current: p.current, total: p.total, jobId });
        lastStatus = s.data.status;
        lastJob = s.data;
        if (lastStatus === "done" || lastStatus === "error") done = true;
      }

      if (lastStatus === "error") {
        throw new Error(lastJob?.error || "Extraction job failed");
      }

      const r = await axios.get(`${API}/pdf-tools/jobs/${jobId}/excel`, { responseType: "blob", timeout: 60000 });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url; a.download = `purchase-bills-${ts}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);

      setDownloadStats({
        processed: lastJob?.processed || r.headers["x-bills-processed"] || files.length,
        success: lastJob?.success || r.headers["x-bills-success"] || 0,
        errors: lastJob?.errors || r.headers["x-bills-errors"] || 0,
        suppliersCreated: lastJob?.suppliers_created || 0,
        suppliersLinked: lastJob?.suppliers_linked || 0,
        duplicatesSkipped: lastJob?.duplicates_skipped || 0,
        productsCreated: lastJob?.products_created || 0,
        fastpathHits: lastJob?.fastpath_hits || 0,
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Extract failed";
      setError(msg);
    } finally {
      setBusy(false); setStep(""); setJobProgress(null);
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="pdf-tools-purchases-page">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-slate-500 text-xs font-bold uppercase tracking-widest">
          <FilePdf size={14} weight="fill" /> PDF Tools
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">Purchase Bill Extractor</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">
          Upload multiple purchase-invoice PDFs — computer-generated <b>or</b> scanned images. The system
          extracts supplier, invoice number, date, HSN, quantity, rate, taxable amount, CGST, SGST, IGST
          and grand total into a multi-sheet Excel workbook. Runs entirely on open-source OCR + the
          bundled AI parser — no external paid service.
        </p>
      </div>

      {/* Upload zone */}
      <div className="bg-white border-2 border-dashed border-slate-300 rounded-sm p-8 text-center hover:border-slate-500 transition-colors"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
      >
        <UploadSimple size={48} weight="duotone" className="mx-auto text-slate-400" />
        <p className="mt-3 text-sm text-slate-600">
          Drag & drop PDF files here, or
          <label className="ml-1 text-slate-900 font-bold underline cursor-pointer">
            browse
            <input
              type="file"
              accept="application/pdf,.pdf"
              multiple
              className="hidden"
              data-testid="pdf-file-input"
              onChange={(e) => addFiles(e.target.files)}
            />
          </label>
        </p>
        <p className="text-xs text-slate-400 mt-1">Supported: any GST purchase invoice — vector or scanned</p>
      </div>

      {/* File chips */}
      {files.length > 0 && (
        <div className="mt-4 bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-900">{files.length} file{files.length > 1 ? "s" : ""} queued</h3>
            <button
              onClick={reset}
              className="text-xs text-slate-500 hover:text-slate-900 underline"
              data-testid="pdf-clear-all-btn"
            >Clear all</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {files.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 rounded-sm text-xs font-mono text-slate-700">
                <FilePdf size={14} weight="fill" className="text-rose-500" />
                <span className="max-w-[220px] truncate">{f.name}</span>
                <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-slate-900 ml-1">
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Options */}
      {files.length > 0 && (
        <div className="mt-4 bg-slate-50 border border-slate-200 rounded-sm p-4 space-y-3">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="save-to-purchases"
              data-testid="save-to-purchases-toggle"
              checked={saveToPurchases}
              onChange={(e) => setSaveToPurchases(e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="save-to-purchases" className="text-sm text-slate-700 cursor-pointer">
              Also save each parsed bill into the <b>Purchases</b> module (creates a PUR-##### row per bill)
            </label>
          </div>
          {saveToPurchases && (
            <div className="flex items-center gap-3 flex-wrap pl-7" data-testid="pdf-godown-section">
              <input
                type="checkbox"
                id="pdf-to-godown"
                data-testid="pdf-to-godown-toggle"
                checked={toGodown}
                onChange={(e) => setToGodown(e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="pdf-to-godown" className="text-sm text-slate-700 cursor-pointer">
                Send goods to <b>Godown</b> (stock-in records)
              </label>
              {toGodown && (
                <>
                  <select value={godownName} data-testid="pdf-godown-select"
                    onChange={(e) => setGodownName(e.target.value)}
                    className="h-8 px-2 border border-slate-300 rounded-sm text-sm font-semibold">
                    <option value="">— New / type name →</option>
                    {godowns.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
                  </select>
                  <input value={godownName} data-testid="pdf-godown-name-input"
                    onChange={(e) => setGodownName(e.target.value)} placeholder="Main Godown"
                    className="h-8 px-2 border border-slate-300 rounded-sm text-sm w-40" />
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Action row */}
      {files.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            onClick={runPreview}
            disabled={busy}
            data-testid="pdf-preview-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm border border-slate-300 text-slate-800 text-sm font-bold hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Eye size={18} weight="bold" /> Preview Parsed Data
          </button>
          <button
            onClick={runExtractAndDownload}
            disabled={busy}
            data-testid="pdf-extract-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <DownloadSimple size={18} weight="bold" /> Extract & Download Excel
          </button>
        </div>
      )}

      {busy && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-sm p-4 text-sm text-blue-800" data-testid="job-progress-banner">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="flex-1">{step || "Working…"}</span>
            {jobProgress && jobProgress.jobId === null && jobProgress.total > 0 && (
              <span className="font-mono text-xs font-bold">{jobProgress.current + 1} / {jobProgress.total} · {jobProgress.pct || 0}%</span>
            )}
            {jobProgress && jobProgress.jobId && jobProgress.total > 0 && (
              <span className="font-mono text-xs font-bold">{jobProgress.current} / {jobProgress.total} pages</span>
            )}
          </div>
          {jobProgress && jobProgress.jobId === null && jobProgress.total > 0 && (
            <div className="mt-2 h-2 rounded-full bg-blue-100 overflow-hidden">
              <div
                data-testid="upload-progress-bar"
                className="h-full bg-emerald-500 transition-all"
                style={{ width: `${Math.min(100, Math.round(((jobProgress.current + (jobProgress.pct || 0) / 100) / jobProgress.total) * 100))}%` }}
              />
            </div>
          )}
          {jobProgress && jobProgress.jobId && jobProgress.total > 0 && (
            <div className="mt-2 h-2 rounded-full bg-blue-100 overflow-hidden">
              <div
                data-testid="job-progress-bar"
                className="h-full bg-blue-500 transition-all"
                style={{ width: `${Math.min(100, Math.round((jobProgress.current / jobProgress.total) * 100))}%` }}
              />
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-6 bg-rose-50 border border-rose-200 rounded-sm p-4 text-sm text-rose-800 flex items-center gap-2">
          <WarningCircle size={18} weight="fill" /> <span>{error}</span>
        </div>
      )}

      {downloadStats && (
        <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-sm p-4 text-sm text-emerald-900 flex items-center gap-2" data-testid="pdf-download-success">
          <CheckCircle size={18} weight="fill" />
          <span>
            Excel downloaded — <b>{downloadStats.success}</b> parsed successfully
            {downloadStats.errors > 0 ? <>, <b>{downloadStats.errors}</b> flagged for review</> : null}
            {saveToPurchases ? <> · saved to Purchases module <FloppyDisk size={14} weight="fill" className="inline align-text-bottom" /></> : null}
            {saveToPurchases && (downloadStats.suppliersCreated > 0 || downloadStats.suppliersLinked > 0) ? (
              <span data-testid="supplier-link-stats"> · <b>{downloadStats.suppliersCreated}</b> new supplier{downloadStats.suppliersCreated === 1 ? "" : "s"} added to Customers, <b>{downloadStats.suppliersLinked}</b> linked to existing</span>
            ) : null}
            {saveToPurchases && downloadStats.productsCreated > 0 ? (
              <span data-testid="product-create-stats"> · <b>{downloadStats.productsCreated}</b> new product{downloadStats.productsCreated === 1 ? "" : "s"} added to Products + Stock Ledger updated</span>
            ) : null}
            {saveToPurchases && downloadStats.duplicatesSkipped > 0 ? (
              <span data-testid="duplicate-skip-stats" className="text-amber-700"> · <b>{downloadStats.duplicatesSkipped}</b> duplicate bill{downloadStats.duplicatesSkipped === 1 ? "" : "s"} skipped (already imported)</span>
            ) : null}
          </span>
        </div>
      )}

      {/* Preview cards */}
      {preview && preview.bills && (
        <div className="mt-8 space-y-4" data-testid="pdf-preview-results">
          <h2 className="text-lg font-bold text-slate-900">Preview — {preview.bills.length} bill{preview.bills.length > 1 ? "s" : ""}</h2>
          {preview.bills.map((b, i) => {
            const p = b.parsed;
            const conf = p?.confidence ?? 0;
            const confColor = conf > 0.75 ? "bg-emerald-100 text-emerald-700" : conf > 0.45 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700";
            return (
              <div key={i} className="bg-white border border-slate-200 rounded-sm p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-mono text-xs text-slate-500">{b.source}</div>
                    {p && <div className="text-lg font-bold text-slate-900 mt-1">{p.supplier_name || "Unknown supplier"}</div>}
                  </div>
                  {p ? (
                    <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-sm ${confColor}`}>
                      Conf: {(conf * 100).toFixed(0)}% · {b.mode}
                    </span>
                  ) : (
                    <span className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-sm bg-rose-100 text-rose-700">Error</span>
                  )}
                </div>
                {b.error && <div className="text-sm text-rose-700 mt-1">{b.error}</div>}
                {p && (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mt-3">
                      <Info label="Invoice No" value={p.invoice_no} />
                      <Info label="Date" value={p.invoice_date} />
                      <Info label="GSTIN" value={p.supplier_gstin} />
                      <Info label="Address" value={p.supplier_address} />
                      <Info label="GST Type" value={p.gst_type} />
                      <Info label="Subtotal" value={`Rs. ${Number(p.subtotal || 0).toFixed(2)}`} />
                      <Info label="CGST" value={`Rs. ${Number(p.cgst || 0).toFixed(2)}`} />
                      <Info label="SGST" value={`Rs. ${Number(p.sgst || 0).toFixed(2)}`} />
                      <Info label="IGST" value={`Rs. ${Number(p.igst || 0).toFixed(2)}`} />
                    </div>
                    <div className="mt-4 border-t border-slate-100 pt-3">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Line Items</div>
                      <div className="overflow-x-auto sticky-table">
                        <table className="min-w-full text-xs">
                          <thead className="sticky-thead bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                            <tr>
                              <th className="px-2 py-2 text-left">Description</th>
                              <th className="px-2 py-2 text-left">HSN</th>
                              <th className="px-2 py-2 text-right">Qty</th>
                              <th className="px-2 py-2 text-left">Unit</th>
                              <th className="px-2 py-2 text-right">Rate</th>
                              <th className="px-2 py-2 text-right">GST %</th>
                              <th className="px-2 py-2 text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {(p.items || []).map((it, j) => (
                              <tr key={j}>
                                <td className="px-2 py-1.5 text-slate-800">{it.description}</td>
                                <td className="px-2 py-1.5 font-mono text-slate-500">{it.hsn_sac}</td>
                                <td className="px-2 py-1.5 text-right font-mono">{it.quantity}</td>
                                <td className="px-2 py-1.5 text-slate-500">{it.unit || "PCS"}</td>
                                <td className="px-2 py-1.5 text-right font-mono">{Number(it.price || 0).toFixed(2)}</td>
                                <td className="px-2 py-1.5 text-right font-mono">{it.gst_rate}%</td>
                                <td className="px-2 py-1.5 text-right font-mono font-bold">{Number(it.amount || 0).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t border-slate-300 bg-slate-50">
                              <td colSpan={6} className="px-2 py-2 text-right font-bold text-slate-900">Grand Total</td>
                              <td className="px-2 py-2 text-right font-mono font-black text-slate-900">Rs. {Number(p.total || 0).toFixed(2)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const Info = ({ label, value }) => (
  <div>
    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
    <div className="text-slate-800 font-mono">{value || <span className="text-slate-300">—</span>}</div>
  </div>
);
